import Vue from "vue";
import VueHtmlToPaper from "vue-html-to-paper";

// Detect base URL for CSS paths (works in both dev and production)
// Note: This should NOT detect Vue routes, only actual deployment subdirectories
function getBaseUrl() {
  // Try to get base URL from document base tag (most reliable)
  const baseTag = document.querySelector("base");
  if (baseTag && baseTag.href) {
    try {
      const baseUrl = new URL(baseTag.href);
      const pathname = baseUrl.pathname.replace(/\/$/, ""); // Remove trailing slash
      // Only return if it's a real base path (not just "/")
      if (pathname && pathname !== "/") {
        return pathname;
      }
    } catch (e) {
      // Invalid base URL, fall back to detection
    }
  }

  // For Vue SPAs, we should NOT use the route path as base URL
  // CSS files are always at the root, regardless of the current route
  // Only detect actual deployment subdirectories (like /pos/, /customer/)
  // These would typically be set in the base tag or be consistent across all routes

  // Check if we're in a subdirectory by looking at where index.html is served from
  // If the app is at root, all routes will work, but CSS should always be at /css/
  // If the app is in a subdirectory, the base tag should be set

  // Default to root (empty string) - CSS files are always at /css/ from root
  return "";
}

const baseUrl = getBaseUrl();

// Helper to construct CSS path (ensures no double slashes)
// Use absolute URLs to ensure they work in print window context
function getCssPath(filename) {
  // Use full URL with origin to ensure it works in print window
  const origin = window.location.origin;
  let path;

  if (baseUrl) {
    // Ensure no double slashes
    path = `${baseUrl}/css/${filename}`.replace(/\/+/g, "/");
  } else {
    path = `/css/${filename}`;
  }

  // Return full URL
  return `${origin}${path}`;
}

const options = {
  name: "_blank",
  specs: ["fullscreen=no", "titlebar=no", "scrollbars=no"],
  styles: [
    // getCssPath("app.css"),
    getCssPath("main.css"),
    getCssPath("vuetify.css"),
    getCssPath("fonts.css"),
    getCssPath("print.css"),
  ],
  timeout: 2500, // increased timeout to allow Chinese fonts to load
  autoClose: true, // close the print window right after print() returns (dialog dismissed or kiosk-printing spooled)
  windowTitle: window.document.title, // override the window title
};

Vue.use(VueHtmlToPaper, options);

// Store original $htmlToPaper method
const originalHtmlToPaper = Vue.prototype.$htmlToPaper;

// Wrap $htmlToPaper to apply locale class to print window
Vue.prototype.$htmlToPaper = function (selector, options = {}) {
  // Detect current locale class from main document
  const localeClasses = ["font-en", "font-th", "font-la", "font-ch"];
  const currentLocaleClass =
    localeClasses.find((cls) =>
      document.documentElement.classList.contains(cls)
    ) || "font-en";

  // Derive lang attribute from locale class
  const localeClassToLang = {
    "font-en": "en",
    "font-th": "th",
    "font-la": "la",
    "font-ch": "zh",
  };
  const currentLang = localeClassToLang[currentLocaleClass] || "en";

  // Call original method
  const result = originalHtmlToPaper.call(this, selector, options);

  // Apply locale class to print window after it's created
  // Use the timeout from options or default to 100ms after the plugin's timeout
  const applyLocaleDelay = (options.timeout || 1000) + 100;

  setTimeout(() => {
    // Find all windows and check which one is the print window
    // The print window should have the selector element in it
    try {
      // Get all open windows
      const windows = [];
      // We can't directly enumerate windows, so we'll use a different approach
      // Monitor for newly created windows
      const checkWindow = (attempt = 0) => {
        if (attempt > 20) return; // Stop after 2 seconds (20 * 100ms)

        // Try to find the print window by checking if it has our stylesheets
        // This is a heuristic - the print window should have the styles we specified
        const allFrames = [window];
        try {
          // Check iframes and popups
          for (let i = 0; i < window.frames.length; i++) {
            try {
              allFrames.push(window.frames[i]);
            } catch (e) {
              // Cross-origin frame, skip
            }
          }
        } catch (e) {
          // Ignore
        }

        // Check each frame for print window characteristics
        allFrames.forEach((win) => {
          try {
            if (
              win &&
              win.document &&
              win.document.documentElement &&
              win !== window &&
              win.document.querySelector(`#${selector}`)
            ) {
              // This looks like our print window
              const htmlElement = win.document.documentElement;
              // Remove existing locale classes
              localeClasses.forEach((cls) => {
                htmlElement.classList.remove(cls);
              });
              // Apply current locale class
              htmlElement.classList.add(currentLocaleClass);
            }
          } catch (e) {
            // Window might be closed or cross-origin, skip
          }
        });

        // Try again if we haven't found it yet
        setTimeout(() => checkWindow(attempt + 1), 100);
      };

      checkWindow();
    } catch (e) {
      console.warn("Could not apply locale class to print window:", e);
    }
  }, applyLocaleDelay);

  return result;
};

// Intercept window.open to apply locale class to print windows
const originalWindowOpen = window.open;

window.open = function (...args) {
  const newWindow = originalWindowOpen.apply(this, args);

  // Check if this is a print window creation (empty URL with _blank target)
  // This is how vue-html-to-paper creates print windows
  if (newWindow && args[0] === "" && args[1] === "_blank") {
    // Detect current locale class from main document
    const localeClasses = ["font-en", "font-th", "font-la", "font-ch"];
    const currentLocaleClass =
      localeClasses.find((cls) =>
        document.documentElement.classList.contains(cls)
      ) || "font-en";

    // Derive lang attribute from locale class
    // Map: font-en -> "en", font-th -> "th", font-la -> "la", font-ch -> "zh"
    const localeClassToLang = {
      "font-en": "en",
      "font-th": "th",
      "font-la": "la",
      "font-ch": "zh", // Chinese uses "zh" as standard HTML lang code
    };
    const currentLang = localeClassToLang[currentLocaleClass] || "en";

    // Intercept document.write to modify HTML before it's written
    try {
      const originalDocumentWrite = newWindow.document.write;
      let htmlTagProcessed = false;

      newWindow.document.write = function (html) {
        // Modify the HTML string to include locale class in <html> tag
        let modifiedHtml = html;

        // Inject Google Fonts directly into <head> to ensure they load in production
        // This is more reliable than @import in CSS files
        if (
          /<head/i.test(modifiedHtml) &&
          !modifiedHtml.includes("fonts.googleapis.com")
        ) {
          const googleFontsLink = `
    <!-- Google Fonts: Poppins, Noto Sans Thai, Noto Sans Lao Looped, Noto Sans SC -->
    <link rel="preconnect" href="https://fonts.googleapis.com" />
    <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
    <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@100;200;300;400;500;600;700;800;900&family=Noto+Sans+Thai:wght@100;200;300;400;500;600;700;800;900&family=Noto+Sans+Lao+Looped:wght@100;200;300;400;500;600;700;800;900&family=Noto+Sans+SC:wght@100;200;300;400;500;600;700;800;900&display=swap" rel="stylesheet" />
    <!-- Font loading script for Chinese characters -->
    <script>
      // Wait for fonts to be ready before allowing print
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(function() {
          document.documentElement.classList.add('fonts-loaded');
        });
      } else {
        // Fallback for browsers without Font Loading API
        setTimeout(function() {
          document.documentElement.classList.add('fonts-loaded');
        }, 1500);
      }
    </script>
`;
          // Insert Google Fonts after <head> tag
          modifiedHtml = modifiedHtml.replace(
            /(<head[^>]*>)/i,
            `$1${googleFontsLink}`
          );
        }

        // Only process if we haven't processed the html tag yet, or if this chunk contains it
        if (!htmlTagProcessed || /<html/i.test(html)) {
          // Pattern 1: <html> with attributes (lang, class, etc.)
          if (/<html\s+[^>]*>/i.test(modifiedHtml)) {
            modifiedHtml = modifiedHtml.replace(
              /<html\s+([^>]*?)>/i,
              (match, attributes) => {
                htmlTagProcessed = true;
                let attrs = (attributes || "").trim();

                // Remove existing locale classes from class attribute
                if (attrs.includes("class=")) {
                  attrs = attrs.replace(
                    /class=["']([^"']*?)["']/i,
                    (classMatch, classValue) => {
                      // Remove existing locale classes
                      let newClassValue = classValue;
                      localeClasses.forEach((cls) => {
                        newClassValue = newClassValue
                          .replace(new RegExp(`\\b${cls}\\b`, "gi"), "")
                          .trim();
                      });
                      // Add current locale class
                      newClassValue = newClassValue
                        ? `${newClassValue} ${currentLocaleClass}`.trim()
                        : currentLocaleClass;
                      return `class="${newClassValue}"`;
                    }
                  );
                } else {
                  // Add class attribute
                  attrs = `${attrs} class="${currentLocaleClass}"`.trim();
                }

                // Ensure lang attribute is set
                if (!/lang=/i.test(attrs)) {
                  attrs = `lang="${currentLang}" ${attrs}`.trim();
                } else {
                  // Update lang if it exists
                  attrs = attrs.replace(
                    /lang=["'][^"']*["']/gi,
                    `lang="${currentLang}"`
                  );
                }

                return `<html ${attrs}>`;
              }
            );
          }
          // Pattern 2: <html> without any attributes
          else if (/<html>/i.test(modifiedHtml)) {
            htmlTagProcessed = true;
            modifiedHtml = modifiedHtml.replace(
              /<html>/i,
              `<html lang="${currentLang}" class="${currentLocaleClass}">`
            );
          }
        }

        // Call original document.write with modified HTML
        return originalDocumentWrite.call(this, modifiedHtml);
      };
    } catch (e) {
      // If we can't intercept document.write, fall back to applying after write
      console.warn("Could not intercept document.write, using fallback:", e);
    }

    // Fallback: Apply locale class after document is written
    const applyLocale = (attempt = 0) => {
      if (attempt > 30) {
        return; // Stop after 3 seconds (30 * 100ms)
      }

      try {
        // Check if window is still open and accessible
        if (!newWindow || newWindow.closed) {
          return;
        }

        const doc = newWindow.document;
        if (!doc || !doc.documentElement) {
          // Document not ready yet, try again
          setTimeout(() => applyLocale(attempt + 1), 100);
          return;
        }

        const htmlElement = doc.documentElement;

        // Check if document has been written to
        if (doc.readyState === "loading") {
          // Document still loading, try again
          setTimeout(() => applyLocale(attempt + 1), 100);
          return;
        }

        // Ensure Google Fonts are loaded (fallback if not injected via document.write)
        const head = doc.head || doc.getElementsByTagName("head")[0];
        if (head && !head.querySelector('link[href*="fonts.googleapis.com"]')) {
          const preconnect1 = doc.createElement("link");
          preconnect1.rel = "preconnect";
          preconnect1.href = "https://fonts.googleapis.com";
          head.appendChild(preconnect1);

          const preconnect2 = doc.createElement("link");
          preconnect2.rel = "preconnect";
          preconnect2.href = "https://fonts.gstatic.com";
          preconnect2.crossOrigin = "anonymous";
          head.appendChild(preconnect2);

          const fontsLink = doc.createElement("link");
          fontsLink.rel = "stylesheet";
          fontsLink.href =
            "https://fonts.googleapis.com/css2?family=Poppins:wght@100;200;300;400;500;600;700;800;900&family=Noto+Sans+Thai:wght@100;200;300;400;500;600;700;800;900&family=Noto+Sans+Lao+Looped:wght@100;200;300;400;500;600;700;800;900&family=Noto+Sans+SC:wght@100;200;300;400;500;600;700;800;900&display=swap";
          head.appendChild(fontsLink);
        }

        // Document is ready, apply locale class and lang attribute
        // Remove existing locale classes
        localeClasses.forEach((cls) => {
          htmlElement.classList.remove(cls);
        });
        // Apply current locale class
        htmlElement.classList.add(currentLocaleClass);
        // Apply lang attribute (derived from locale class)
        htmlElement.setAttribute("lang", currentLang);
      } catch (e) {
        // Window might be closed or cross-origin, stop trying
        if (e.name !== "SecurityError") {
          console.warn("Could not apply locale class to print window:", e);
        }
      }
    };

    // Start applying after a short delay to ensure window is initialized
    // Then continue checking until document is ready
    setTimeout(() => applyLocale(), 100);
  }

  return newWindow;
};
